import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useIsMobile } from "@/lib/useMediaQuery";

export interface ToastOptions {
  /** Shows an Undo button; running it dismisses the toast. */
  undo?: () => void;
  /** How long the toast stays up. Undoable ones get longer. */
  ms?: number;
}

type Show = (msg: string, opts?: ToastOptions) => void;

const ToastContext = createContext<Show>(() => {});

interface Toast {
  msg: string;
  undo?: () => void;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);
  const mobile = useIsMobile();
  const timer = useRef<number | undefined>(undefined);

  const dismiss = useCallback(() => {
    window.clearTimeout(timer.current);
    setToast(null);
  }, []);

  const show = useCallback<Show>((msg, opts) => {
    window.clearTimeout(timer.current);
    setToast({ msg, undo: opts?.undo });
    // An undoable toast is a decision point, so it lingers. The window has to
    // outlast "oh — no, wait", which 2.6s does not.
    timer.current = window.setTimeout(() => setToast(null), opts?.ms ?? (opts?.undo ? 7000 : 2600));
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {toast && (
        <div
          role="status"
          style={mobile
            ? {
              // §15.4 — a banner from the top, the way iOS notifies.
              position: "fixed", top: "calc(env(safe-area-inset-top) + 8px)", left: 12, right: 12,
              background: "var(--surface)", color: "var(--ink)", padding: "13px 16px", borderRadius: 14,
              border: "1px solid var(--line)", fontSize: 13, fontWeight: 500, zIndex: 100,
              boxShadow: "0 10px 30px var(--shadow)", animation: "slidedown 0.18s ease-out",
              display: "flex", alignItems: "center", gap: 12,
            }
            : {
              position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)",
              background: "var(--ink)", color: "var(--bg2)", padding: "11px 18px", borderRadius: 8,
              fontSize: 12.5, zIndex: 100, boxShadow: "0 8px 26px rgba(28,27,25,0.2)",
              animation: "fadein 0.14s ease-out",
              display: "flex", alignItems: "center", gap: 14,
            }}
        >
          <span style={{ flex: 1, minWidth: 0 }}>{toast.msg}</span>
          {toast.undo && (
            <button
              onClick={() => { const fn = toast.undo!; dismiss(); fn(); }}
              style={{
                flex: "none", border: "none", background: "none", cursor: "pointer",
                padding: 0, fontSize: mobile ? 13 : 12.5, fontWeight: 600,
                textDecoration: "underline",
                color: mobile ? "var(--accent)" : "var(--bg2)",
              }}
            >
              Undo
            </button>
          )}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
